import { z } from "zod";
import { firstDuplicateKey } from "./duplicate-keys";
import {
  customFieldsLimit,
  customTextFieldDefaultMaxLength,
  unreadableCustomFields,
  type CustomFieldAnswer,
  type CustomFieldDefinition,
  type StoredCustomFields,
} from "./types";

const fieldKeySchema = z.string().regex(/^[a-z][a-z0-9_]{0,31}$/);

const textFieldSchema = z.object({
  key: fieldKeySchema,
  label: z.string().min(1),
  type: z.literal("text"),
  required: z.boolean().default(false),
  maxLength: z.number().int().min(1).max(500).default(customTextFieldDefaultMaxLength),
});

const numberFieldSchema = z
  .object({
    key: fieldKeySchema,
    label: z.string().min(1),
    type: z.literal("number"),
    required: z.boolean().default(false),
    min: z.number().int().nullable().default(null),
    max: z.number().int().nullable().default(null),
  })
  .refine((field) => field.min === null || field.max === null || field.min <= field.max);

export const customFieldDefinitionSchema = z.union([textFieldSchema, numberFieldSchema]);

export const customFieldDefinitionsSchema = z
  .array(customFieldDefinitionSchema)
  .max(customFieldsLimit)
  .refine(hasUniqueKeys);

export const customFieldAnswerSchema = z.object({
  key: fieldKeySchema,
  label: z.string().min(1),
  value: z.union([z.string(), z.number()]),
});

export const customFieldAnswersSchema = z.array(customFieldAnswerSchema).refine(hasUniqueKeys);

/** Null for anything unreadable as well as for a stored null — for callers with
 *  nothing different to do with the two. Where the difference is load-bearing,
 *  `parseStoredCustomFields` keeps them apart. The API validates on write and is
 *  where a partner gets an error they can act on. */
export function parseCustomFieldDefinitions(value: unknown): CustomFieldDefinition[] | null {
  const parsed = customFieldDefinitionsSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** The stored value has three readings, not two: the constraint behind it only
 *  guarantees "array of at most eight", so rows this schema refuses do exist and
 *  must not be read as an absence — read that way they would be shown as unset
 *  and wiped by the next unrelated save. */
export function parseStoredCustomFields(value: unknown): StoredCustomFields {
  if (value == null) return null;
  return parseCustomFieldDefinitions(value) ?? unreadableCustomFields;
}

export function parseCustomFieldAnswers(value: unknown): CustomFieldAnswer[] | null {
  const parsed = customFieldAnswersSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function hasUniqueKeys(entries: { key: string }[]): boolean {
  return firstDuplicateKey(entries.map((entry) => entry.key)) === null;
}
